<template>
  <div class="h-full min-h-full">
    <div class="flex-1 pb-8 z-0">
      <div class="bg-white shadow">
        <div class="px-4 sm:px-6 lg:mx-auto lg:px-8 max-w-7xl">
          <div class="pt-6 pb-6 md:flex md:items-center justify-between lg:border-t lg:border-cool-gray-200">
            <div class="text-2xl font-bold leading-7 text-cool-gray-900 sm:leading-9 sm:truncate">
              {{ isEditMode ? 'Edit Course' : 'New Course' }}
            </div>

            <div class="flex">
              <SecondaryButton
                text="Back"
                class="mr-2"
                @click="handleBack"
              />
              <PrimaryButton
                :text="isEditMode ? 'Update Course' : 'Create Course'"
                icon="fal fa-save"
                :disabled="!isValid || loading"
                :loading="loading"
                @click="handleSubmit"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="px-4 sm:px-6 lg:mx-auto lg:px-8 max-w-7xl ml-auto mr-auto">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <!-- Course Details Section -->
        <div class="md:col-span-1">
          <div class="px-4 sm:px-0">
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              Course Details
            </h3>
            <p class="mt-1 text-sm leading-5 text-gray-600">
              We need to know a few things about your course.
            </p>
          </div>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <div class="shadow sm:rounded-md">
            <div class="px-4 py-5 bg-white sm:p-6 rounded-lg">
              <div class="col-span-6 sm:col-span-4">
                <label class="block text-sm font-medium leading-5 text-gray-700">
                  Course Name <span class="text-red-700">*</span>
                </label>
                <div class="block pb-1 text-sm leading-5 text-gray-400">
                  Enter the name of your course
                </div>
                <div v-if="showValidation && !formData.name" class="block pb-1 text-sm leading-5 text-red-700">
                  This field is required
                </div>
                <input
                  v-model="formData.name"
                  type="text"
                  placeholder="Enter course name"
                  class="mt-1 form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 max-w-sm"
                  @input="validateForm"
                >
              </div>

              <div class="col-span-6 sm:col-span-4 mt-5">
                <label class="block text-sm font-medium leading-5 text-gray-700">
                  Interval (minutes) <span class="text-red-700">*</span>
                </label>
                <div class="block pb-1 text-sm leading-5 text-gray-400">
                  Time interval between tee times (e.g., 10, 15, 20)
                </div>
                <div v-if="showValidation && !formData.interval" class="block pb-1 text-sm leading-5 text-red-700">
                  This field is required
                </div>
                <input
                  v-model.number="formData.interval"
                  type="number"
                  placeholder="Enter interval in minutes"
                  class="mt-1 form-input block transition duration-150 ease-in-out sm:text-sm sm:leading-5 w-40"
                  min="1"
                  @input="validateForm"
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Course Configuration Section -->
      <div class="md:grid md:grid-cols-3 md:gap-6 mt-6">
        <div class="md:col-span-1">
          <div class="px-4 sm:px-0">
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              Course Configuration
            </h3>
            <p class="mt-1 text-sm leading-5 text-gray-600">
              The course configuration will determine the availability for your course.
            </p>
          </div>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <div class="shadow sm:rounded-md">
            <div class="px-4 py-5 bg-white sm:p-6 rounded-lg">
              <div class="col-span-6 sm:col-span-4">
                <label class="block text-sm font-medium leading-5 text-gray-700">
                  Configuration Name <span class="text-red-700">*</span>
                </label>
                <div class="block pb-1 text-sm leading-5 text-gray-400">
                  Name for this course configuration
                </div>
                <div v-if="showValidation && !formData.configName" class="block pb-1 text-sm leading-5 text-red-700">
                  This field is required
                </div>
                <input
                  v-model="formData.configName"
                  type="text"
                  placeholder="Course configuration"
                  class="mt-1 form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 max-w-sm"
                  @input="validateForm"
                >
              </div>

              <div class="col-span-6 sm:col-span-4 mt-5">
                <label class="block text-sm font-medium leading-5 text-gray-700">
                  Number of Holes <span class="text-red-700">*</span>
                </label>
                <div class="block pb-1 text-sm leading-5 text-gray-400">
                  Total number of holes that the course has
                </div>
                <div v-if="showValidation && !formData.numberOfHoles" class="block pb-1 text-sm leading-5 text-red-700">
                  This field is required
                </div>
                <input
                  v-model.number="formData.numberOfHoles"
                  type="number"
                  placeholder="18"
                  class="mt-1 form-input block transition duration-150 ease-in-out sm:text-sm sm:leading-5 w-40"
                  min="1"
                  @input="validateForm"
                >
              </div>

              <div class="col-span-6 sm:col-span-4 mt-5">
                <label class="block text-sm font-medium leading-5 text-gray-700">
                  Starting Hole <span class="text-red-700">*</span>
                </label>
                <div class="block pb-1 text-sm leading-5 text-gray-400">
                  The hole that is the starting position
                </div>
                <div v-if="showValidation && !formData.startingHole" class="block pb-1 text-sm leading-5 text-red-700">
                  This field is required
                </div>
                <input
                  v-model.number="formData.startingHole"
                  type="number"
                  placeholder="1"
                  class="mt-1 form-input block transition duration-150 ease-in-out sm:text-sm sm:leading-5 w-40"
                  min="1"
                  :max="formData.numberOfHoles || 18"
                  @input="validateForm"
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import PrimaryButton from '@/app/shared/components/base/button/primaryButton.vue';
import SecondaryButton from '@/app/shared/components/base/button/secondaryButton.vue';
import { httpClient } from '@/app/shared/services/http-client/http-client';

export default {
  name: 'CourseForm',

  components: {
    PrimaryButton,
    SecondaryButton,
  },

  props: {
    courseId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      showValidation: false,
      isValid: false,
      formData: {
        name: '',
        interval: null,
        configName: 'Course configuration',
        numberOfHoles: null,
        startingHole: null,
      },
    };
  },

  computed: {
    ...mapState({
      selectedClub: state => state.club.selectedClub,
      tenantId: state => state.user.tenantId,
    }),

    isEditMode() {
      return !!this.courseId;
    },
  },

  mounted() {
    if (this.isEditMode) {
      this.loadCourse();
    } else {
      this.validateForm();
    }
  },

  methods: {
    validateForm() {
      this.isValid = !!(
        this.formData.name &&
        this.formData.name.trim() !== '' &&
        this.formData.interval &&
        this.formData.interval > 0 &&
        this.formData.configName &&
        this.formData.configName.trim() !== '' &&
        this.formData.numberOfHoles &&
        this.formData.numberOfHoles > 0 &&
        this.formData.startingHole &&
        this.formData.startingHole > 0 &&
        this.formData.startingHole <= this.formData.numberOfHoles
      );
    },

    async loadCourse() {
      // Load course data if editing
      // This would be implemented based on your API
      console.log('Loading course:', this.courseId);
    },

    async handleSubmit() {
      this.showValidation = true;
      this.validateForm();

      if (!this.isValid) {
        return;
      }

      this.loading = true;

      try {
        // Create course
        const coursePayload = {
          name: this.formData.name,
          interval: parseInt(this.formData.interval, 10),
          clubId: this.selectedClub ? this.selectedClub.id : null,
          tenantId: this.tenantId || null,
        };

        console.log('Creating course with payload:', coursePayload);

        const courseUrl = 'services/app/Courses/Create';
        const courseResponse = await httpClient.post(courseUrl, coursePayload);
        const createdCourse = courseResponse.data.result;

        console.log('Course created:', createdCourse);

        // Create course configuration
        if (createdCourse && createdCourse.id) {
          const configPayload = {
            name: this.formData.configName,
            courseId: createdCourse.id,
            numberOfHoles: parseInt(this.formData.numberOfHoles, 10),
            startingHole: parseInt(this.formData.startingHole, 10),
          };

          console.log('Creating course configuration with payload:', configPayload);

          const configUrl = 'services/app/CourseConfiguration/Create';
          await httpClient.post(configUrl, configPayload);

          console.log('Course configuration created');
        }

        // Change route back to Courses and emit success event
        this.$router.push({ path: '/Courses' });
        this.$emit('success', createdCourse);
      } catch (error) {
        console.error('Error creating course:', error);
        alert('Failed to create course. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    handleBack() {
      // Change route back to Courses without query parameter
      this.$router.push({ path: '/Courses' });
      this.$emit('back');
    },
  },
};
</script>
